import { createFileRoute } from "@tanstack/react-router";
import { ServiceDetail } from "@/components/ServiceDetail";
import { serviceFaqs } from "@/data/serviceFaqs";
import { serviceGraph, abs } from "@/lib/serviceSchema";
import img from "@/assets/denver-retrofit-cable-fishing.webp.asset.json";
import img800 from "@/assets/retrofit-800.webp.asset.json";
import img1200 from "@/assets/retrofit-1200.webp.asset.json";

const bullets = [
  "In-wall and in-ceiling speaker installation in finished rooms",
  "TV relocations and clean in-wall HDMI / power runs",
  "Cat6 / fiber drops to home offices, media rooms, and APs",
  "Camera, doorbell, and access control cable fishing",
  "Smart shade, lighting, and thermostat low-voltage wiring",
  "Drop cloths, dust containment, and protected floors on every job",
];

export const Route = createFileRoute("/services/retrofit")({
  head: () => ({
    meta: [
      { title: "Retrofit & Cable Fishing | Apex AV Denver" },
      {
        name: "description",
        content:
          "Expert retrofit cable fishing in finished Denver homes. Add speakers, TVs, networking, cameras, and smart home wiring through existing walls and ceilings — clean, minimal patching, no mess left behind.",
      },
      { name: "keywords", content: "retrofit wiring Denver, cable fishing Denver, low voltage retrofit Denver, finished home wiring Denver, in-wall speaker retrofit Denver, TV cable concealment Denver, Cat6 retrofit Denver, camera wire fishing Denver, smart home retrofit Denver, clean cable installation Denver" },
      { property: "og:title", content: "Retrofit & Cable Fishing — Apex AV Denver" },
      {
        property: "og:description",
        content: "Add wiring to finished homes without tearing them apart.",
      },
      { property: "og:url", content: "https://apexav.com/services/retrofit" },
      { property: "og:type", content: "website" },
      { property: "og:image", content: abs(img800.url) },
      { name: "twitter:image", content: abs(img800.url) },
      { name: "twitter:title", content: "Retrofit & Cable Fishing — Apex AV Denver" },
      { name: "twitter:description", content: "Add wiring to finished homes without tearing them apart." },
    ],

    links: [{ rel: "canonical", href: "https://apexav.com/services/retrofit" }, { rel: "preload", as: "image", href: img800.url, imageSrcSet: `${img800.url} 800w, ${img1200.url} 1200w, ${img.url} 1900w`, imageSizes: "100vw", fetchPriority: "high" } as never],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceGraph({
            slug: "retrofit",
            name: "Retrofit & Cable Fishing",
            description: "Add speakers, TVs, networking, and smart home wiring to finished Denver homes.",
            pageTitle: "Retrofit & Cable Fishing | Apex AV Denver",
            pageDescription:
              "Expert retrofit cable fishing in finished Denver homes. Add speakers, TVs, networking, cameras, and smart home wiring through existing walls and ceilings — clean, minimal patching, no mess left behind.",
            image: img800.url,
            faqs: serviceFaqs["retrofit"],
            serviceType: "Retrofit & Cable Fishing",
            breadcrumbName: "Retrofit & Cable Fishing",
            offerCatalog: bullets,
          }),
        ),
      },
    ],
  }),
  component: () => (
    <ServiceDetail
      faqs={serviceFaqs["retrofit"]}
      topics={["prewire", "av-installation", "tv-mounting", "whole-home-audio"]}
      eyebrow="Retrofit"
      title="New wiring. No torn-up home."
      image={img800.url}
      imageSrcSet={`${img800.url} 800w, ${img1200.url} 1200w, ${img.url} 1900w`}
      imageSizes="100vw"
      imageAlt="Retrofit low-voltage cable fishing and clean wire installation in a Highlands Ranch Denver home"
      intro={<>Already moved in? We specialize in <strong>retrofit wiring in Denver</strong> and clean <strong>cable fishing</strong> — running low-voltage wiring through finished walls, ceilings, and floors with the smallest possible access cuts.</>}


      bullets={bullets}
      features={[
        {
          title: "Minimal access cuts",
          desc: "Strategic, small openings — never the holes you'd expect for a job this clean.",
        },
        {
          title: "Whole-home capable",
          desc: "Two-story homes, finished basements, vaulted ceilings, brick — we've fished it all.",
        },
        {
          title: "Spotless cleanup",
          desc: "We leave with your home cleaner than we found it — dust contained, floors protected.",
        },
      ]}
    />
  ),
});
